import { useCallback, useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useTenantMembershipsQuery } from '../api/auth/queries';
import { useSwitchTenantMutation } from '../api/auth/mutations';
import type { TenantSwitchRequest } from '../api/auth/interfaces';
import { canUseAdminTenantScope } from '@/utils/authz';

export interface TenantContext {
  clientId: string | null;
  companyId: string | null;
  roles: string[];
  isLoaded: boolean;
}

export function useTenant() {
  const { payload, isAuthenticated } = useAuth();
  const [tenantContext, setTenantContext] = useState<TenantContext>({
    clientId: null,
    companyId: null,
    roles: [],
    isLoaded: false
  });

  // Only enable tenant memberships query for non-admin users
  const isAdmin = canUseAdminTenantScope(payload?.role);
  const shouldFetchMemberships = isAuthenticated && !isAdmin;
  const { data: memberships, isLoading: membershipsLoading } = useTenantMembershipsQuery(shouldFetchMemberships);
  const switchTenantMutation = useSwitchTenantMutation();

  // Update tenant context from JWT payload
  useEffect(() => {
    if (payload) {
      setTenantContext({
        clientId: payload.clientId,
        companyId: payload.companyId,
        roles: payload.tenantRoles || [],
        isLoaded: true
      });
    } else {
      setTenantContext({
        clientId: null,
        companyId: null,
        roles: [],
        isLoaded: true
      });
    }
  }, [payload]);

  const switchToTenant = useCallback(async (request: TenantSwitchRequest) => {
    try {
      const result = await switchTenantMutation.mutateAsync(request);
      // The mutation will automatically update the access token
      // The useAuth hook will pick up the new token and update the context
      return result;
    } catch (error) {
      console.error('Failed to switch tenant:', error);
      throw error;
    }
  }, [switchTenantMutation]);

  const getAvailableTenants = useCallback(() => {
    if (!memberships) return { clients: [], companies: [] };
    
    return {
      clients: memberships.clients || [],
      companies: memberships.companies || []
    };
  }, [memberships]);

  const hasRole = useCallback((role: string) => {
    return tenantContext.roles.includes(role);
  }, [tenantContext.roles]);

  const hasAnyRole = useCallback((roles: string[]) => {
    return roles.some(role => tenantContext.roles.includes(role));
  }, [tenantContext.roles]);

  const isInTenant = useCallback((clientId?: string, companyId?: string) => {
    if (companyId) {
      return tenantContext.companyId === companyId;
    }
    if (clientId) {
      return tenantContext.clientId === clientId;
    }
    return false;
  }, [tenantContext.clientId, tenantContext.companyId]);

  return {
    tenantContext,
    memberships,
    membershipsLoading,
    switchToTenant,
    getAvailableTenants,
    hasRole,
    hasAnyRole,
    isInTenant,
    isSwitching: switchTenantMutation.isPending,
    switchError: switchTenantMutation.error
  };
}
